import React from 'react';
import { WifiOff, CloudOff, Wifi, RefreshCw } from 'lucide-react';
import { Button } from '@parsina/framework';
import { C, CT } from '@parsina/framework/clinical/src/constants/theme';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { ConnectionState } from '../../hooks/useOnlineStatus';

// ─────────────────────────────────────────────────────────────────────────────
// The one place the app says «you are not connected».
//
// Before this, a dropped connection produced N independent error cards, one per
// panel, each blaming itself, none of them explaining the others — and no signal
// at all when it came back. The doctor's only strategy was to refresh and see.
//
// Deliberate choices:
//
//   It is a BAR, not a modal or a toast. A modal would block a doctor from
//   reading the notes already on screen, which are still perfectly good and are
//   the most valuable thing in the room during an outage. A toast would vanish
//   while the condition persisted. A bar states an ongoing fact and lets work
//   continue around it.
//
//   It sits at the top and pushes content down rather than floating over it.
//   Overlaying would cover the page header on every screen — and on the visit
//   screen that is the patient's name, which is not something to hide.
//
//   Recovery is announced ONCE and then disappears on its own. A permanent
//   «متصل» is chrome nobody reads; the useful signal is the transition, and
//   specifically the moment a doctor can stop wondering whether their last save
//   went through.
// ─────────────────────────────────────────────────────────────────────────────

const RECOVERY_VISIBLE_MS = 4_000;

const VARIANTS = {
  [ConnectionState.OFFLINE]: {
    Icon: WifiOff,
    bg: C.red,
    fg: '#fff',
    messageKey: 'errors.connectivity.offline',
    testId: 'connectivity-offline',
  },
  [ConnectionState.DEGRADED]: {
    Icon: CloudOff,
    bg: C.amber,
    // Amber is a light hue; white on it fails contrast. `CT.warnInk` is the
    // token the kit already pairs with a warning surface — this used to be a
    // hand-mixed brown, which is how a second, slightly-off warning colour
    // enters a design system.
    fg: CT.warnInk,
    messageKey: 'errors.connectivity.degraded',
    testId: 'connectivity-degraded',
  },
};

export default function ConnectivityBanner({ state, recovered, onAcknowledgeRecovery, onRetry, busy }) {
  const { t } = useTranslation();
  const disconnected = state !== ConnectionState.ONLINE;

  // The recovery bar retires itself. The timer is keyed on the flag so a second
  // outage inside the window restarts it rather than inheriting a stale
  // countdown that would hide the confirmation early.
  React.useEffect(() => {
    if (!recovered || disconnected) return undefined;
    const timer = setTimeout(() => onAcknowledgeRecovery?.(), RECOVERY_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [recovered, disconnected, onAcknowledgeRecovery]);

  if (disconnected) {
    const variant = VARIANTS[state];
    if (!variant) return null;
    const { Icon, bg, fg, messageKey, testId } = variant;

    return (
      <Bar bg={bg} fg={fg} testId={testId} state={state}>
        <Icon size={15} strokeWidth={2.2} aria-hidden />
        <span>{t(messageKey)}</span>
        {onRetry && (
          // The kit `Button` at `sm`, not a hand-rolled pill. It sits on a
          // coloured bar rather than the page, so `currentColor` carries the
          // bar's ink through — the only thing the variants cannot know — and
          // everything else (height, radius, type, focus ring, disabled state)
          // comes from the kit like every other button in the app.
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onRetry}
            disabled={busy}
            data-testid="connectivity-retry"
            style={{
              marginInlineStart: 'auto',
              color: fg,
              border: '1px solid currentColor',
              background: 'transparent',
              gap: '0.3125rem',
            }}
          >
            <RefreshCw
              size={12}
              aria-hidden
              style={busy ? { animation: 'connectivity-spin 0.9s linear infinite' } : undefined}
            />
            {t(busy ? 'errors.connectivity.checking' : 'common.actions.retry')}
          </Button>
        )}
      </Bar>
    );
  }

  if (recovered) {
    return (
      <Bar bg={C.green} fg="#fff" testId="connectivity-restored" state="restored">
        <Wifi size={15} strokeWidth={2.2} aria-hidden />
        <span>{t('errors.connectivity.restored')}</span>
      </Bar>
    );
  }

  return null;
}

function Bar({ bg, fg, testId, state, children }) {
  return (
    <div
      data-testid={testId}
      data-connection-state={state}
      role="status"
      // `polite`, not `assertive`. A doctor mid-sentence in a clinical note must
      // not have their screen reader interrupted by an infrastructure notice —
      // the connection dropping is not more urgent than what they are writing.
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.4375rem 1rem',
        background: bg,
        color: fg,
        ...type.label,
        flexShrink: 0,
        zIndex: 40,
      }}
    >
      <style>{'@keyframes connectivity-spin { to { transform: rotate(360deg) } }'}</style>
      {children}
    </div>
  );
}
